/**
 * Pure derivation of the organisation-panel super-property bag (PIX-605).
 *
 * The panel counterpart to eventProperties.js, kept separate because the two
 * take different payloads: a student's user-info object versus a panel user
 * plus their membership list plus which organisation they are currently acting
 * on. Pure and side-effect free so it unit tests in plain node (PIX-607).
 *
 * PANEL ACCESS IS PER ORGANISATION. A person holds a role in each organisation
 * separately and may hold roles in several, so there is no such thing as "their
 * role" - only their role *here*. The existing student-side derivation takes
 * the first organisation in the list, which is wrong for anyone teaching at two
 * schools: two thirds of their activity would be labelled with the wrong
 * school. Everything below keys off the SELECTED organisation instead.
 *
 * PRIVACY: the panel user payload carries `email` and `full_name`. Neither may
 * appear in the output. Only the opaque uuid and non-identifying context leaves
 * the browser.
 */
import { deriveBoardContext } from './eventProperties.js';
import { PROPS, SURFACES } from './events.js';

/**
 * The organisation the user is currently acting on, or null.
 *
 * Returns null rather than guessing when nothing is selected - including for a
 * user with exactly one membership. The store auto-selects a sole organisation,
 * so that state is transient; guessing would only paper over the window before
 * it resolves, at the cost of being confidently wrong for the multi-school case
 * the API explicitly refuses to guess at (it returns no default when there is a
 * genuine choice).
 */
export function deriveSelectedOrganisation({
  organisations = [],
  selectedOrganisationId = null,
} = {}) {
  if (!selectedOrganisationId || !Array.isArray(organisations)) {
    return null;
  }

  return (
    organisations.find((org) => org?.uuid === selectedOrganisationId) ?? null
  );
}

/**
 * Normalise a role to lowercase so a breakdown never fragments into
 * `Admin`/`ADMIN`/`admin` for the same role. The API returns them uppercased;
 * the store already lowercases for its own getters, and this keeps analytics
 * consistent with that rather than with the wire format.
 */
function normaliseRole(role) {
  return typeof role === 'string' && role ? role.toLowerCase() : null;
}

/**
 * Build the panel super-property bag.
 *
 * Deliberately does NOT write `is_organisation_user` or `organisation_role`.
 * Those already exist and already mean something else - "this *student*
 * belongs to a school" - and existing student insights filter on them. Writing
 * panel values into them would silently change what those insights count,
 * without anyone touching an insight. New names instead.
 *
 * Board is explicitly null rather than absent. A teacher has no single active
 * board - it is passed per event from local context instead. Reusing
 * deriveBoardContext rather than hardcoding nulls keeps one decision in one
 * place, and makes this bag self-contained: the panel store identifies from a
 * router *guard*, which runs before the navigation hook that clears board, so
 * without this there is a window where a persisted student board is still
 * registered.
 *
 * @param {object}   input
 * @param {object?}  input.user                   panel user payload, or null
 * @param {object[]} input.organisations          memberships granting panel access
 * @param {?string}  input.selectedOrganisationId which one is being acted on
 * @param {?string}  input.environment            deploy environment label
 */
export function deriveOrgSuperProperties({
  user = null,
  organisations = [],
  selectedOrganisationId = null,
  environment = null,
} = {}) {
  const memberships = Array.isArray(organisations) ? organisations : [];
  const selected = deriveSelectedOrganisation({
    organisations: memberships,
    selectedOrganisationId,
  });

  return {
    ...deriveBoardContext({ surface: SURFACES.ORGANISATION }),
    [PROPS.IS_AUTHENTICATED]: Boolean(user),
    // The role held in the organisation being acted on - never a global role.
    [PROPS.ORG_PANEL_ROLE]: normaliseRole(selected?.role),
    // Owners behave differently from invited staff: they chose the product.
    [PROPS.IS_ORG_OWNER]: Boolean(selected?.is_owner),
    // How many schools this person runs the panel for. A multi-school teacher
    // is a different user from a single-school one, and worth separating.
    [PROPS.ORGANISATION_COUNT]: memberships.length,
    [PROPS.APP_ENVIRONMENT]: environment ?? null,
  };
}
